/**
 * garmentName — a SKU, in the words a subcontractor would use for it.
 *
 * Mikko, 2026-09-22, carrying the CEO's revision: the payout's work table
 * drops CODE, leads with the DESIGN, and gains a DESCRIPTION — "its per
 * DESIGN i.e SS028 its Intal Scrubsuit Elite White Small … another example
 * DESIGN SS090 Intal Scrubsuit Elite Pro Nude Pink Medium."
 *
 * So the paper stops speaking in rate-band shorthand. E3 and SS028 tell a
 * payroll clerk what was priced; they tell the person who sewed it nothing.
 * "Intal Scrubsuit Elite Midnight Blue Small" is the garment they held.
 *
 * THREE PARTS, THREE SOURCES, ALL ALREADY IN THE SKU.
 *   SS028-MDBLU-S  ->  parent SS028 · colour MDBLU · size S
 *   parent  -> the product line, from the catalog every other screen uses
 *              (production-grid/parentCategories.ts — 135 rows, embedded,
 *              the same table the hub's VBA carries)
 *   colour  -> COLOUR_NAMES below
 *   size    -> SIZE_NAMES below
 *
 * THE COLOUR TABLE WAS READ OFF THE REAL CATALOG, not invented. Every code
 * here was extracted from the IGCom SKU catalog's own product titles
 * ("… - S / Midnight Blue"), kept only where the code is a plausible
 * abbreviation of the name it claims — a subsequence test, which is what
 * throws out the rows whose title puts the SIZE after the slash instead.
 * Eighty entries rather than the catalog's thousands, because a COMBI code is
 * composed from its parts: OXBLU-NVBLU is "Oxford Blue - Navy Blue" without
 * needing a row of its own, and a combination nobody has sold yet still
 * names itself.
 *
 * AN UNKNOWN CODE PRINTS ITSELF. A colour the catalog has never seen reads as
 * "MDBLU" rather than as nothing: a code somebody can look up beats a blank
 * on a document they are signing. Same for a parent the catalog does not
 * list — the description is then the colour and size alone, which is still
 * more than the SKU said.
 *
 * Pure, and deliberately import-light: the catalog map and nothing else.
 */
import { PARENT_CATEGORY } from "../production-grid/parentCategories";

/**
 * The catalogue's colour vocabulary now lives in `lib/skuColours` — the
 * cutting floor's W.O. Log and production tags read the same table, so it
 * belongs to neither module. Re-exported here so every existing caller and
 * test is untouched.
 */
import { COLOUR_NAMES, colourName } from "../../lib/skuColours";
export { COLOUR_NAMES, colourName };

/** The sizes the floor cuts, spelled out. */
export const SIZE_NAMES: Readonly<Record<string, string>> = {
  XXS: "2X-Small", XS: "X-Small", S: "Small", M: "Medium", L: "Large",
  XL: "X-Large", "2XL": "2X-Large", "3XL": "3X-Large", "4XL": "4X-Large",
  "5XL": "5X-Large",
};

/**
 * The product line's words. Scrub lines are scrubsuits; the two that are not
 * say what they are, because "Scrubsuit Nurse Uniform" is a garment nobody
 * makes.
 */
const CATEGORY_WORDS: Readonly<Record<string, string>> = {
  CLASSIC: "Scrubsuit Classic",
  ELITE: "Scrubsuit Elite",
  "ELITE PRO": "Scrubsuit Elite Pro",
  ALTA: "Scrubsuit Alta",
  PRIMO: "Scrubsuit Primo",
  "NURSE UNIFORM": "Nurse Uniform",
  "DOCTORS COAT": "Doctors Coat",
  "LAB GOWN": "Lab Gown",
};

/** The maker's name, on every line of the description. */
export const BRAND = "Intal";

const up = (v: string | null | undefined): string => String(v ?? "").trim().toUpperCase();

/**
 * A colour token's trailing PLAN SUFFIX — "MDBLU-TO" is Midnight Blue TOP
 * ONLY, "MDBLU-PO" is Midnight Blue PANTS ONLY (Mikko, 2026-09-22).
 *
 * It is not part of the colour and never prints: "make sure to pull out color
 * only dont include PANTS AND TOP ONLY in description". The garment is already
 * under a TOP or a PANTS section on the sheet, so the suffix would only say
 * again, in a code, what the section heading says in a word.
 *
 * WORTH KNOWING WHERE IT IS NOT USED: `wipPlan.resolveWipPlan` decides TOP
 * ONLY / PANTS ONLY / set from a person's choice and then the Cutting J.O.
 * Log, and NEVER from the SKU — so a WIP whose own code says PANTS ONLY still
 * falls through to an assumed "BOTH" and is priced a top band it did not earn.
 * That is money, not presentation, so it is reported rather than changed here.
 */
export function splitPlanSuffix(colour: string): { colour: string; plan: "TOP" | "PANTS" | "" } {
  const c = up(colour);
  if (c.endsWith("-TO")) return { colour: c.slice(0, -3), plan: "TOP" };
  if (c.endsWith("-PO")) return { colour: c.slice(0, -3), plan: "PANTS" };
  return { colour: c, plan: "" };
}

export interface ISkuParts {
  /** "SS028" — the design, which is what the ITEM column prints. */
  parent: string;
  /** "MDBLU", or "" when the SKU carries no colour. */
  colour: string;
  /** "S", or "" when the SKU carries no size. */
  size: string;
}

/**
 * Split a SKU CODE into its three parts.
 *
 * `SS075-MRN-BLK-XS` is a four-token SKU whose colour is two tokens, so the
 * rule is first-token-is-parent, last-is-size, everything between is colour —
 * never "split on the second dash", which loses every combi.
 *
 * A SKU with no dashes is a bare design: parent only, and the description
 * falls back to the product line.
 */
export function parseSku(sku: string | null | undefined): ISkuParts {
  const parts = up(sku).split("-").map(t => t.trim()).filter(Boolean);
  if (parts.length === 0) return { parent: "", colour: "", size: "" };
  if (parts.length === 1) return { parent: parts[0], colour: "", size: "" };
  const last = parts[parts.length - 1];
  // A two-token SKU whose tail is a size is parent+size; otherwise it is
  // parent+colour, which is how the sizeless designs are written.
  const tailIsSize = last in SIZE_NAMES;
  return {
    parent: parts[0],
    colour: parts.slice(1, tailIsSize ? -1 : undefined).join("-"),
    size: tailIsSize ? last : "",
  };
}

/** A size code in words, or the code itself. */
export function sizeName(code: string | null | undefined): string {
  const s = up(code);
  return s ? SIZE_NAMES[s] ?? s : "";
}

/** The product line a design belongs to, in words. "" when unknown. */
export function categoryWords(parent: string | null | undefined): string {
  const cat = PARENT_CATEGORY[up(parent)];
  return cat ? CATEGORY_WORDS[up(cat)] ?? "" : "";
}

/**
 * The whole description: "Intal Scrubsuit Elite Pro Nude Pink Medium".
 *
 * Every part is optional and the sentence closes up around what is missing,
 * because a payout must print SOMETHING for a SKU nobody has catalogued —
 * degrading to "Intal Scrubsuit Elite" or to "" is honest, and an exception
 * here would take out the whole document over one unfamiliar bundle.
 */
export function garmentName(sku: string | null | undefined): string {
  const { parent, colour, size } = parseSku(sku);
  const words = [BRAND, categoryWords(parent), colourName(colour), sizeName(size)];
  return words.map(w => w.trim()).filter(Boolean).join(" ");
}

/** Cut order, so a row reads S, M, L rather than L, M, S. */
/**
 * ONE LINE FOR A WHOLE BAND, grouped by PRODUCT LINE.
 *
 * Mikko, 2026-09-22, setting the final grammar:
 *
 *     Intal Scrubsuit Elite • MOSGRN-M, BLK-S, WHT-XL, ARGRN-XXS
 *
 * TWO SEPARATORS, NOT THREE, and each does one job:
 *   the BOLD DOT  " • "  divides the product line from the garments it covers
 *   the COMMA     ", "   divides one garment from the next
 *   (a band spanning more than one product line still uses " | " between them)
 *
 * It replaced a three-level grammar — sizes by comma, colours by a middle dot,
 * lines by a pipe — that asked the reader to hold three ranks of punctuation in
 * their head to parse one cell. Two ranks is what the content actually has:
 * this line, these garments.
 *
 * THE GARMENTS ARE NAMED BY THEIR SKU SUFFIX, not in words. "MOSGRN-M" is
 * exactly what is printed on the bag the subcontractor delivered, so the cell
 * can be matched against the goods CHARACTER BY CHARACTER rather than
 * translated first. It is also far shorter than "Moss Green M", which is what
 * buys the bigger type back. `garmentName` still spells a SINGLE garment out in
 * full — one garment on its own can afford the words, a band of nine cannot.
 *
 * Because nothing is translated, NOTHING IS DROPPED: the old build skipped any
 * colour missing from the lookup table, so an unrecognised code silently left
 * a garment off a document somebody signs. A code now always prints itself.
 *
 * The shared leading words of the line names are FACTORED OUT to the front, so
 * a band of Scrubsuit Elite / Scrubsuit Primo / Scrubsuit Elite Pro reads
 * "Intal Scrubsuit Elite • … | Primo • … | Elite Pro • …" rather than
 * repeating "Scrubsuit" three times. A single line factors its whole name out
 * and has no pipe. A band mixing a scrubsuit with a doctor's coat shares no
 * leading word, so nothing is factored and each segment keeps its full name.
 *
 * ORDER IS FIRST APPEARANCE, for the lines and the garments alike. `skuQty` is
 * sorted by SKU, so SS046 (Elite), SS075 (Primo), SS090 (Elite Pro) arrive in
 * that order, and within a line the colours arrive in their own SKU order.
 *
 * MINIMAL DISTINCT SET. Mikko, 2026-09-22: "we can stick to first page … we
 * will eliminate redundant/too identical Description … make it as one." Every
 * identical colour+size collapses to one entry, so what remains is genuine
 * variety with no repeats. REDUNDANCY, NOT TRUNCATION, IS THE LEVER: dedup
 * makes the description as short as it can truthfully be, and there is NO
 * arbitrary cap — every distinct garment is named.
 *
 * What is left over is length the data genuinely has, and `td.desc` in
 * payslipDoc WRAPS it rather than clipping it — an ellipsis on this column cuts
 * the part a subcontractor checks their delivery against. The page stays honest
 * because payslipDoc prices each row in the lines it takes.
 *
 * FALLS BACK TO THE PRODUCT LINE when there are no garments to summarise.
 * Every payout paid before 2026-09-22, and the whole imported workbook history,
 * stored lines with no `skuQty` — so without a fallback the column is blank on
 * almost every real payout ("no Description here", Mikko). A line still carries
 * its `designs`, so the lines can be named even when the garments cannot: an
 * old multi-line payout reads "Intal Scrubsuit Elite | Primo | Elite Pro", a
 * single-line one "Intal Scrubsuit Elite". No bold dot on those — there is
 * nothing on the far side of it.
 *
 * Returns "" only when neither garments nor designs name a known line.
 */

/** The em dash between a design and the garments under it. */
export const LABEL_DASH = " \u2014 ";

/**
 * The product FAMILIES, longest first. The description names the family and
 * NOT the line (Mikko, 2026-09-22: "since its different caetgory with same
 * rate we can remove the Elite, Alta, Elite Pro in Description") — a band can
 * cover Elite and Alta at one rate, so naming the line repeated it on every
 * segment while saying nothing the rate had not already settled.
 */
const FAMILIES: readonly string[] = ["Scrubsuit", "Nurse Uniform", "Doctors Coat", "Lab Gown"];

/** "Scrubsuit Elite Pro" -> "Scrubsuit"; a name with no family passes through. */
function familyOf(words: string): string {
  for (const f of FAMILIES) if (words === f || words.startsWith(f + " ")) return f;
  return words;
}

/**
 * How a band's garments are ARRANGED in the cell. The facts are identical in
 * all three; only the order and the grouping differ.
 *
 *   Design 1 (colour-names)  SS032 - Black XXS, Nude Pink 2XL, Royal Blue M
 *   Design 2 (per-garment)   SS032 - BLK-XXS, NDPNK-2XL, RYBLU-M
 *
 * Mikko picked BOTH and wants both on the print (2026-09-22): "the print would
 * have two designs, Design 1 is the Draft 2, Design 2 is the Draft 1". The
 * names are for a reader; the codes are for matching a bag label character by
 * character. Which one a payout prints is a setting, not a deploy.
 *
 * The FAMILY ("Intal Scrubsuit") is the same words on every row of every
 * payout — the masthead already carries the mark, and the ITEM already carries
 * the design — so whether it repeats is worth a page on a long payout and
 * nothing at all on a short one.
 */
export type DescStyle = "colour-names" | "per-garment";

/**
 * FOUR DESIGNS, from TWO independent choices (Mikko, 2026-09-22: "Design 3 is
 * the same format of Design 1 but without Design in Description since its
 * already Design in ITEM, then Design 4 is the same format of Design 2 but
 * also without Design in Description").
 *
 *   STYLE   — colour NAMES for a reader, or SKU CODES to match a bag label.
 *   DESIGN  — whether each segment is labelled with its design.
 *
 * They are independent, so they are stored independently and the four numbers
 * are just the four combinations. Writing four styles instead would double
 * `DESC_LINE_CHARS` for nothing: dropping the label shortens the text, it does
 * not change how the text WRAPS, and the wrap tables are what the style keys.
 */
export const DESC_DESIGNS: readonly {
  id: DescDesign; style: DescStyle; showDesign: boolean; label: string;
}[] = [
  { id: 1, style: "colour-names", showDesign: true, label: "Design 1 \u2014 colour names, with design (SS028 \u2014 Black XXS)" },
  { id: 2, style: "per-garment", showDesign: true, label: "Design 2 \u2014 SKU codes, with design (SS028 \u2014 BLK-XXS)" },
  { id: 3, style: "colour-names", showDesign: false, label: "Design 3 \u2014 colour names, no design (Black XXS)" },
  { id: 4, style: "per-garment", showDesign: false, label: "Design 4 \u2014 SKU codes, no design (BLK-XXS)" },
];

export type DescDesign = 1 | 2 | 3 | 4;

/** Anything to one of the four, so a stored string or a stale number is safe. */
export const asDescDesign = (design: unknown): DescDesign => {
  const n = Number(design);
  return n === 2 || n === 3 || n === 4 ? (n as DescDesign) : 1;
};

/** The style a stored design number means; Design 1 when it says nothing. */
export const descStyleOf = (design: unknown): DescStyle =>
  DESC_DESIGNS[asDescDesign(design) - 1].style;

/**
 * Whether the DESCRIPTION labels each segment with its design.
 *
 * Off on 3 and 4 because the ITEM beside it already lists the designs, in the
 * same order — the segments line up positionally, so the label is the second
 * time the cell says the same thing, on the column with no room to spare. The
 * SEMICOLON still separates one design's garments from the next, so which
 * garments belong together is not lost with the label.
 */
export const descShowsDesign = (design: unknown): boolean =>
  DESC_DESIGNS[asDescDesign(design) - 1].showDesign;
/**
 * Between two DESIGNS in one band. A SEMICOLON, never a pipe — Mikko,
 * 2026-09-22: "dont put | its vertical lign that would invalid the finance
 * format, only horizontal lines."
 */
export const BAND_SEP = "; ";

/** Cut order, so XXS sorts before XS and 2XL after XL. */
const SIZE_ORDER: readonly string[] = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
const sizeRank = (code: string): number => {
  const i = SIZE_ORDER.indexOf(up(code));
  return i < 0 ? SIZE_ORDER.length : i;
};

interface ILineGroup {
  /** The DESIGN this group is for ("SS028"), or "" on the design fallback. */
  design: string;
  /** The product line's words ("Scrubsuit Elite"), or "" for an unknown parent. */
  line: string;
  /** "MOSGRN-M" -> itself, deduped; sorted on the way out. Empty on the fallback. */
  codes: Set<string>;
}

/**
 * The band's garments, folded into one group PER DESIGN.
 *
 * Mikko, 2026-09-22: "im seeing not properly categorized here see SS028 and
 * SS032 and SS041, its scattered, make sure its lookup properly to
 * DESIGN/PARENT SKU." Grouping by PRODUCT LINE — which this did — poured every
 * garment of every Elite design into one run, so a band covering SS028 and
 * SS032 read as one undifferentiated list with no way to tell which design a
 * garment came off. The DESIGN is the unit: it is what the ITEM column lists
 * and what the rate band prices.
 *
 * Groups come out in DESIGN ORDER, the order the ITEM column is in, so the two
 * columns read across. The design FALLBACK still folds by line, because with
 * no garments to attach a design code says nothing the ITEM does not.
 */
function groupByDesign(
  skus: readonly { sku: string }[] | undefined,
  designs: readonly string[] | undefined,
): ILineGroup[] {
  const order: ILineGroup[] = [];
  const byKey = new Map<string, ILineGroup>();
  const ensure = (key: string, design: string, line: string): ILineGroup => {
    let g = byKey.get(key);
    if (!g) { g = { design, line, codes: new Set() }; byKey.set(key, g); order.push(g); }
    return g;
  };
  if (skus && skus.length) {
    for (const { sku } of skus) {
      const { parent, colour, size } = parseSku(sku);
      const g = ensure(up(parent), up(parent), categoryWords(parent));
      // The SKU's own suffix, minus the TOP ONLY / PANTS ONLY marker, which is
      // the garment's PLAN and not its colour (see splitPlanSuffix). Never
      // translated beyond that, so an unrecognised colour can never be dropped
      // off a document somebody signs.
      const code = [splitPlanSuffix(colour).colour, up(size)].filter(Boolean).join("-");
      if (code) g.codes.add(code);
    }
    order.sort((a, b) => a.design.localeCompare(b.design));
  } else if (designs && designs.length) {
    for (const d of designs) {
      // The line stores the bare code; the catalog is keyed on the SS-prefixed
      // parent, so add the prefix a design that lacks one would need.
      const parent = up(d).startsWith("SS") ? up(d) : `SS${up(d)}`;
      const line = categoryWords(parent);
      if (line) ensure(line, "", line);   // design fallback: named, no garments
    }
  }
  return order;
}

/** A group's garments, arranged the way the chosen design asks for. */
function garmentsOf(g: ILineGroup, style: DescStyle): string {
  const codes = sortedCodes(g);
  if (style !== "colour-names") return codes.join(", ");
  // REAL COLOUR NAMES, the same arrangement otherwise (Mikko, 2026-09-22:
  // "instead of KHK, WHT it would be Khaki, White ... meaning real color
  // name"). The SIZE stays a code: nobody writes "Extra Extra Small" on a bag,
  // and the colour is what a delivery is checked by eye against. A code the
  // catalog has never heard of prints itself rather than vanishing.
  return codes.map(code => {
    const i = code.lastIndexOf("-");
    const colour = i < 0 ? code : code.slice(0, i);
    const size = i < 0 ? "" : code.slice(i + 1);
    return [colourName(colour) || colour, size].filter(Boolean).join(" ");
  }).join(", ");
}

/**
 * A group's garments, COLOUR BY COLOUR and in cut order within a colour.
 *
 * Insertion order is whatever order the payout's SKUs happened to arrive in,
 * which on a real band is no order at all — "MDBLU-TO-XXS, MDBLU-PO-XXS,
 * MDBLU-PO-XS, MSGRN-M, SLGRY-M, VLT-M, MSTRD-M, APPCH-M, MLGRN-M" was the
 * scattering Mikko saw. Sorted, a colour's sizes sit together and the eye can
 * find one.
 */
function sortedCodes(g: ILineGroup): string[] {
  const split = (code: string): [string, string] => {
    const i = code.lastIndexOf("-");
    return i < 0 ? [code, ""] : [code.slice(0, i), code.slice(i + 1)];
  };
  return [...g.codes].sort((a, b) => {
    const [ac, az] = split(a), [bc, bz] = split(b);
    return ac !== bc ? ac.localeCompare(bc) : sizeRank(az) - sizeRank(bz);
  });
}

/** The leading words every named line shares, for factoring to the front. */
function commonLeadingWords(lines: readonly string[]): string[] {
  const toks = lines.filter(Boolean).map(n => n.split(" "));
  if (toks.length === 0) return [];
  const out: string[] = [];
  for (let i = 0; i < toks[0].length; i++) {
    const w = toks[0][i];
    if (toks.every(t => t[i] === w)) out.push(w); else break;
  }
  return out;
}

export function garmentDescription(
  skus: readonly { sku: string; qty?: number }[] | undefined,
  designs?: readonly string[],
  style: DescStyle = "colour-names",
  showDesign = true,
): string {
  const groups = groupByDesign(skus, designs);
  if (groups.length === 0) return "";

  // NO FAMILY AND NO PRODUCT LINE on a band that has garments. "Intal
  // Scrubsuit" was the same words on every row of every payout, and then
  // briefly in the column heading; Mikko took it off there too (2026-09-22).
  // The masthead carries the mark and the ITEM carries the design, so the cell
  // is garments and nothing else.
  //
  // AND ON DESIGNS 3 AND 4, NOT EVEN THE DESIGN. The ITEM lists them in this
  // same order, so the segments line up with it positionally and the label is
  // the cell repeating its neighbour. The SEMICOLON stays either way: it is
  // what keeps one design's garments together.
  const segments = groups.map(g => {
    const codes = garmentsOf(g, style);
    if (!codes) return "";
    return showDesign ? `${g.design}${LABEL_DASH}${codes}` : codes;
  }).filter(Boolean);
  if (segments.length > 0) return segments.join(BAND_SEP).trim();

  // THE FALLBACK, which has no garments to name. Every payout paid before
  // 2026-09-22 and the whole imported history stored lines with no skuQty, so
  // without this the column is blank on almost all of them ("no Description
  // here", Mikko). All that is left is the product LINE, and there the line is
  // the only thing worth saying — shared leading words factored to the front
  // so a three-line band does not repeat "Scrubsuit" three times.
  const lines = groups.map(g => g.line).filter(Boolean);
  if (lines.length === 0) return "";
  const shared = commonLeadingWords(lines);
  const tails = lines.map(l => l.split(" ").slice(shared.length).join(" ").trim()).filter(Boolean);
  return [shared.join(" "), tails.join(BAND_SEP)].filter(Boolean).join(" ").trim();
}